import { FC } from "react";
import { Bell, BookOpen, ChevronRight, Coins } from "lucide-react";

export interface SidebarToolsCreditsProps {
  /** 千分位积分文案 */
  text: string;
  /** 点击积分主区 → 积分明细弹窗 */
  onClick: () => void;
  /** 点击「购买」→ 充值弹窗 */
  onBuy: () => void;
  /** 管理员隐藏购买入口 */
  canBuy: boolean;
}

export interface SidebarToolsNotificationProps {
  /** 未读数，0 不展示红点 */
  unreadCount: number;
  onClick: () => void;
}

export interface SidebarToolsProps {
  credits: SidebarToolsCreditsProps;
  /** 为 false 时不渲染积分行（无积分权限） */
  showCredits?: boolean;
  notification: SidebarToolsNotificationProps;
  /** 为 false 时不渲染通知行（无通知权限） */
  showNotification?: boolean;
  /** 使用指南 → 新标签打开飞书 wiki */
  onOpenGuide: () => void;
  /** 为 false 时不渲染使用指南行（无使用指南权限） */
  showGuide?: boolean;
}

/**
 * 侧边栏底部入口区：积分 / 使用指南 / 通知 三行（样式对齐 doc/workspace-shell.css）。
 * 纯展示组件，数据与回调由 HomeShell 注入，不感知权限 / 角色。
 */
export const SidebarTools: FC<SidebarToolsProps> = ({
  credits,
  showCredits = true,
  notification,
  showNotification = true,
  onOpenGuide,
  showGuide = true,
}) => {
  const { text, onClick, onBuy, canBuy } = credits;
  const { unreadCount, onClick: onNotices } = notification;
  const badge =
    unreadCount > 0 ? (
      <b className="sidebar-tool-badge">{unreadCount > 99 ? "99+" : unreadCount}</b>
    ) : null;

  return (
    <div className="sidebar-tools">
      {showCredits ? (
        <div className="sidebar-tool-row sidebar-tool-row--points">
          <button
            type="button"
            className="sidebar-tool-main"
            aria-label="查看积分明细"
            onClick={onClick}
          >
            <Coins size={15} strokeWidth={2} aria-hidden />
            <span>积分</span>
            <strong>{text}</strong>
          </button>
          {canBuy ? (
            <button type="button" className="sidebar-tool-action" onClick={onBuy}>
              购买
            </button>
          ) : null}
        </div>
      ) : null}

      {showGuide ? (
        <button
          type="button"
          className="sidebar-tool-row"
          aria-label="使用指南（新标签页打开）"
          onClick={onOpenGuide}
        >
          <BookOpen size={15} strokeWidth={2} aria-hidden />
          <span>使用指南</span>
          <ChevronRight size={13} strokeWidth={2} className="sidebar-tool-chevron" aria-hidden />
        </button>
      ) : null}

      {showNotification ? (
        <button
          type="button"
          className="sidebar-tool-row"
          aria-label={`通知${unreadCount > 0 ? `，${unreadCount} 条未读` : ""}`}
          onClick={onNotices}
        >
          <Bell size={15} strokeWidth={2} aria-hidden />
          <span>通知</span>
          {badge}
        </button>
      ) : null}
    </div>
  );
};
